import numeral from 'numeral';
import GlobalConst from './GlobalConst';
import _ from 'lodash';

const oneTrillion = 1000000000000;
const oneBillion = 1000000000;
const oneMillion = 1000000;
const oneThousand = 1000;
const isInt = (n) => n % 1 === 0;

export function round(num, point) {
	const number = Number(num);
	if (!_.isNumber(num)) return '-';
	return isInt(number) ? number : Number(parseFloat(number.toFixed(point)));
}

export function roundMoney(num, point) {
	const number = Number(num);
	if (!_.isNumber(num)) return '-';
	return isInt(number) ? `$${number}` : `$${Number(parseFloat(number.toFixed(point)))}`;
}

// Values too small to render at `point` decimals collapse to a bound. Negatives need the
// opposite bound, otherwise a below-zero value reads as if it sat between zero and minValue.
const belowMinValueLabel = (num, minValue) => (num < 0 ? `>-${minValue}` : `<${minValue}`);

export function roundAnStringify(num, point) {
	const minValue = 1.0 / Math.pow(10.0, point);
	if (Math.abs(num) < minValue && num !== 0) return belowMinValueLabel(num, minValue);
	const rounded = round(num, point);
	return String(parseFloat(rounded));
}

export function roundAnStringifyThousandsSeparated(num, point) {
	const minValue = 1.0 / Math.pow(10.0, point);
	if (Math.abs(num) < minValue && num !== 0) return belowMinValueLabel(num, minValue);
	const rounded = round(num, point);
	return thousandsSeparated(rounded, point);
}

export function formatMoney(num, roundDigits = 1, formatter = formatBigNumber) {
	if (!_.isNumber(num) && !_.isString(num)) return '-';
	return `$${formatter(num, roundDigits)}`
}

export function formatMoneyWithCode(num, roundDigits = 1, code) {
	return `${formatBigNumber(num, roundDigits)} ${code}`
}

export function formatMoneyWithCodeSymbol(num, roundDigits = 1, code, symbol, formatter = formatBigNumber) {
	return `${symbol}${formatter(num, roundDigits)} ${code}`
}

export function formatTimestamp(num) {
	if (!num) return '';

	num = num.replace(/\D/g, '').slice(0, 6);

	let h = '', m = '', s = '';
	if (num.length >= 2) {
		h = num.slice(0, 2);
	} else {
		h = num;
	}

	if (num.length >= 4) {
		m = num.slice(2, 4);
	} else if (num.length > 2) {
		m = num.slice(2);
	}

	if (num.length === 6) {
		s = num.slice(4, 6);
	} else if (num.length > 4) {
		s = num.slice(4);
	}

	return [h, m, s].filter(Boolean).join(':');
}

export function formatPercentWithSign(num, roundDigits = 1) {
	return `${num > 0 ? '+' : ''}${formatPercent(num, roundDigits)}`
}

export function formatPercent(num, roundDigits = 1, multiplier = 100.0) {
	if (!_.isNumber(num) && !_.isString(num)) return '-';
	return `${roundAnStringify(num * multiplier, roundDigits)}%`
}

export function formatThousandsPercent(num, roundDigits = 1, multiplier = 100.0) {
	if (!_.isNumber(num) && !_.isString(num)) return '-';
	return `${roundAnStringifyThousandsSeparated(num * multiplier, roundDigits)}%`
}

export function formatBigPercent(num, roundDigits = 1, multiplier = 100.0) {
	if (!_.isNumber(num) && !_.isString(num)) return '-';
	return `${formatBigNumber(roundAnStringify(num * multiplier, roundDigits))}%`
}

export function format0to1Percent(num, roundDigits = 1) {
	return `${roundAnStringify(num, roundDigits)}%`
}

export function formatBigNumberWithSign(num, roundDigits = 1) {
	return `${num > 0 ? '+' : ''}${formatBigNumber(num, roundDigits)}`
}

export function formatBigNumber(number, roundDigits = 1) {
	if (!_.isNumber(number) && !_.isString(number)) return '-';
	const num = Number(number);
	if (Math.abs(num) >= oneTrillion) {
		return roundAnStringify(num / oneTrillion, roundDigits) + 'T';
	}
	if (Math.abs(num) >= oneBillion) {
		return roundAnStringify(num / oneBillion, roundDigits) + 'B';
	}
	if (Math.abs(num) >= oneMillion) {
		return roundAnStringify(num / oneMillion, roundDigits) + 'M';
	}
	if (Math.abs(num) >= oneThousand) {
		return roundAnStringify(num / oneThousand, roundDigits) + 'K';
	}
	return roundAnStringify(num, roundDigits);
}

export function thousandsSeparated(num, roundDigits = 0) {
	const format = roundDigits > 0 ? `0,0.${'0'.repeat(roundDigits)}` : '0,0';
	let formatted = numeral(num).format(format);
	if (formatted.includes('.')) {
		formatted = formatted.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
	}
	return formatted;
}

// Defaults missing or unparseable values to 0, and strips currency/percent decoration so
// display strings can be sorted. Use toFiniteNumberOrNull instead when a missing value must
// stay distinguishable from a real 0.
export function parseNumericField(value) {
	if (_.isNull(value) || _.isUndefined(value)) return 0;
	if (_.isNumber(value)) return Number.isFinite(value) ? value : 0;
	const parsed = Number(String(value).replace(/[$,%\s]/g, ''));
	return Number.isFinite(parsed) ? parsed : 0;
}

// Returns null for missing or unparseable values rather than 0, because `Number(null)` is 0
// and a threshold comparison against that reads absent data as a real zero. Does not strip
// currency or percent characters — pass raw API values, not formatted display strings.
export function toFiniteNumberOrNull(value) {
	if (_.isNull(value) || _.isUndefined(value) || value === '') return null;
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : null;
}

export function thousandsSeparatedPadded(num, roundDigits = 0) {
	const format = roundDigits > 0 ? `0,0.${'0'.repeat(roundDigits)}` : '0,0';
	return numeral(num).format(format);
}

export function expandShorthandNumber(str) {
	const suffixMultiplier = {
	'K': 1e3,
	'M': 1e6,
	'B': 1e9
	};

	const match = str.match(/^([\d.,]+)([KMB])?$/i);
	if (!match) return NaN;

	const [, number, suffix] = match;
	const numericValue = parseFloat(number.replace(',', ''));
	const multiplier = suffix ? suffixMultiplier[suffix.toUpperCase()] : 1;

	return numericValue * multiplier;
}

export function formatterForChartType(chartType, roundDigits = 1, currency) {
	if (chartType == GlobalConst.CHART_TYPE_PERCENT) {
		return val => format0to1Percent(val, roundDigits);
	} else if (chartType == GlobalConst.CHART_TYPE_MONEY) {
		if (_.isString(currency) && currency !== 'USD') return val => formatMoneyWithCode(val, roundDigits, currency);
		if (_.isString(currency) && currency == 'USD') return val => formatMoneyWithCodeSymbol(val, roundDigits, currency, '$');
		return val => formatMoney(val, roundDigits);
	} else if (chartType == GlobalConst.CHART_TYPE_NUMBER_WITH_SIGN) {
		return val => formatBigNumberWithSign(val, roundDigits);
	}
	return val => formatBigNumber(val, roundDigits);
}

export function decimalPlacesCount(num) {
	var match = (''+num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
	if (!match) { return 0; }
	return Math.max(0, (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0));
}

export function imageCompressionRatio(number) {
	const maxSize = 1024*1024*10; // 10MB
	if (number < maxSize) {
		return 1.0;
	}
	return (maxSize/number).toFixed(1);
}

export function formatNumberCurrency(number, callback, roundDigits=1) {
	if (_.isString(number)) {
		const splitted = _.split(number, ' ');
		if (!_.isEmpty(splitted[1])) return `${callback(splitted[0], roundDigits)} ${splitted[1]}`;
		return `${callback(splitted[0], roundDigits)}`;
	}
}
